import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { 
  Paperclip, 
  Download, 
  Eye, 
  Link,
  FileText,
  File,
  Image,
  FileSpreadsheet,
  FileCode,
  Copy,
  User,
  Calendar,
  Shield,
  ChevronRight,
  Trash2
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

interface AttachmentFile {
  id: string;
  name: string;
  type: string;
  size: string;
  url: string;
  timestamp: string;
  uploadedBy?: string;
  previewable?: boolean;
  permissions?: 'all' | 'engineers' | 'client' | 'admin';
  versions?: number;
}

interface AttachmentsSectionProps {
  attachments: AttachmentFile[];
  onDelete?: (index: number) => void;
}

const getFileIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case 'pdf':
      return <FileText className="w-4 h-4 text-red-500" />;
    case 'dwg':
    case 'dxf':
      return <FileCode className="w-4 h-4 text-blue-500" />;
    case 'xlsx':
    case 'xls':
    case 'excel':
      return <FileSpreadsheet className="w-4 h-4 text-green-500" />;
    case 'docx':
    case 'doc':
    case 'word':
      return <FileText className="w-4 h-4 text-blue-600" />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'image':
      return <Image className="w-4 h-4 text-purple-500" />;
    default:
      return <File className="w-4 h-4 text-muted-foreground" />;
  }
};

const getPermissionBadge = (permission: string) => {
  switch (permission) {
    case 'engineers':
      return <Badge variant="secondary" className="text-xs print-badge">Engenheiros</Badge>;
    case 'client':
      return <Badge variant="outline" className="text-xs print-badge">Cliente</Badge>;
    case 'admin':
      return <Badge variant="destructive" className="text-xs print-badge">Admin</Badge>;
    default:
      return <Badge variant="default" className="text-xs print-badge">Todos</Badge>;
  }
};

const isPreviewable = (type: string): boolean => {
  const previewableTypes = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp'];
  return previewableTypes.includes(type.toLowerCase());
};

export const AttachmentsSection: React.FC<AttachmentsSectionProps> = ({ attachments, onDelete }) => {
  const [previewFile, setPreviewFile] = useState<AttachmentFile | null>(null);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);
  const { toast } = useToast();

  const handlePreview = (attachment: AttachmentFile) => {
    if (isPreviewable(attachment.type)) {
      setPreviewFile(attachment);
    } else {
      toast({
        title: "Preview não disponível",
        description: "Este tipo de arquivo não suporta visualização rápida.",
        variant: "destructive"
      });
    }
  };

  const handleCopyLink = async (attachment: AttachmentFile) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${attachment.url}`);
      toast({
        title: "Link copiado",
        description: "Link do arquivo copiado para a área de transferência.",
      });
    } catch (error) {
      toast({
        title: "Erro ao copiar link",
        description: "Não foi possível copiar o link do arquivo.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteAttachment = (index: number) => {
    if (onDelete) {
      onDelete(index);
    }
    setDeleteConfirmIndex(null);
  };

  const confirmDelete = (index: number) => {
    setDeleteConfirmIndex(index);
  };

  const handleDownload = (attachment: AttachmentFile) => {
    // Simulate download
    window.open(attachment.url, '_blank');
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Em modo impressão, sempre renderize a LISTA COMPLETA (sem paginação/scroll)
  const shouldShowAllAttachments = typeof window !== "undefined" && window.matchMedia?.("print")?.matches;

  return (
    <>
      {attachments.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground">
            Nenhum anexo adicionado
          </p>
        </div>
      ) : (
        <div className="space-y-1 print-expand print-attachments-full">
          {attachments.map((attachment, index) => (
            <div
              key={attachment.id}
              className="flex items-start gap-2 p-2 border rounded-lg hover:bg-accent/50 transition-colors print-no-break print-flat"
            >
              <div className="flex-shrink-0 mt-0.5">
                {getFileIcon(attachment.type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => handlePreview(attachment)}
                      className="font-medium text-xs hover:text-primary transition-colors text-left truncate block w-full print-text"
                      disabled={!isPreviewable(attachment.type) || shouldShowAllAttachments}
                    >
                      {attachment.name}
                      {isPreviewable(attachment.type) && !shouldShowAllAttachments && (
                        <Eye className="w-3 h-3 inline ml-1 opacity-60" />
                      )}
                    </button>
                    
                    <div className="flex items-center justify-between gap-2 mt-1 text-xs text-muted-foreground">
                      <div className="flex items-center gap-3">
                        {attachment.uploadedBy && (
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            <span className="print-text">{attachment.uploadedBy}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span className="print-text">{formatDate(attachment.timestamp)}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className="print-text">{attachment.type} • {attachment.size}</span>
                        {attachment.versions && attachment.versions > 1 && (
                          <Badge variant="outline" className="text-xs px-1 py-0 print-badge">
                            v{attachment.versions}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {attachment.permissions && (
                      <div className="flex items-center gap-1 mt-1">
                        <Shield className="w-3 h-3 text-muted-foreground" />
                        {getPermissionBadge(attachment.permissions)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {!shouldShowAllAttachments && (
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopyLink(attachment)}
                    className="h-7 w-7 p-0"
                    title="Copiar link"
                  >
                    <Link className="w-3 h-3" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(attachment)}
                    className="h-7 w-7 p-0"
                    title="Download"
                  >
                    <Download className="w-3 h-3" />
                  </Button>

                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => confirmDelete(index)}
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      title="Excluir anexo"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Eye className="w-4 h-4" />
              {previewFile?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {previewFile && (
              <div className="w-full h-[70vh] bg-muted/20 rounded-lg flex items-center justify-center">
                {previewFile.type.toLowerCase() === 'pdf' ? (
                  <iframe
                    src={previewFile.url}
                    className="w-full h-full rounded-lg"
                    title={previewFile.name}
                  />
                ) : (
                  <img
                    src={previewFile.url}
                    alt={previewFile.name}
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                )}
              </div>
            )}
          </div>
          
          <div className="flex justify-between items-center pt-4 border-t">
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>{previewFile?.type} • {previewFile?.size}</span>
              {previewFile?.uploadedBy && (
                <span>Por: {previewFile.uploadedBy}</span>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => previewFile && handleCopyLink(previewFile)}
              >
                <Copy className="w-3 h-3 mr-1" />
                Copiar Link
              </Button>
              <Button
                size="sm"
                onClick={() => previewFile && handleDownload(previewFile)}
              >
                <Download className="w-3 h-3 mr-1" />
                Download
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteConfirmIndex !== null}
        onOpenChange={() => setDeleteConfirmIndex(null)}
        onConfirm={() => deleteConfirmIndex !== null && handleDeleteAttachment(deleteConfirmIndex)}
        title="Excluir Anexo?"
        message="Tem certeza que deseja excluir este anexo? Esta ação não pode ser desfeita."
      />
    </>
  );
};